import BaseApi, { Response } from './baseApi';
import { ChangePasswordRequest, LoginRequest, LoginResponse, ForgotPasswordRequest, ResetPasswordRequest } from '../dtos/Accounts/index';
import { BaseUser, UpdateUser } from '../dtos/Users/index';

// Re-export DTOs for convenience
export type { ChangePasswordRequest, LoginRequest, LoginResponse };

class AccountApi extends BaseApi {
  private readonly basePath = '/account';

  /**
   * Login user
   * POST /api/account/login
   * @remarks AllowAnonymous - No auth required
   */
  async login(request: LoginRequest): Promise<Response<LoginResponse>> {
    return this.post<LoginResponse, LoginRequest>(`${this.basePath}/login`, request);
  }

  /**
   * Logout user
   * POST /api/account/logout
   * @remarks Authorized - Auth required
   */
  async logout(): Promise<Response<boolean>> {
    return this.post<boolean>(`${this.basePath}/logout`);
  }

  /**
   * Get current user profile
   * GET /api/account/profile
   * @remarks Authorized - Auth required
   */
  async getProfile(): Promise<Response<BaseUser | null>> {
    return this.get<BaseUser | null>(`${this.basePath}/profile`);
  }

  /**
   * Update current user profile
   * PUT /api/account/profile
   * @remarks Authorized - Auth required
   */
  async updateProfile(userData: Omit<UpdateUser, 'id'>): Promise<Response<BaseUser>> {
    return this.put<BaseUser, Omit<UpdateUser, 'id'>>(`${this.basePath}/profile`, userData);
  }

  /**
   * Change password
   * PUT /api/account/change-password
   * @remarks Authorized - Auth required
   */
  async changePassword(request: ChangePasswordRequest): Promise<Response<boolean>> {
    return this.put<boolean, ChangePasswordRequest>(`${this.basePath}/change-password`, request);
  }

  /**
   * Forgot password - Request password reset
   * POST /api/account/forgot-password
   * @remarks AllowAnonymous - No auth required
   */
  async forgotPassword(request: ForgotPasswordRequest): Promise<Response<boolean>> {
    return this.post<boolean, ForgotPasswordRequest>(`${this.basePath}/forgot-password`, request);
  }

  /**
   * Reset password - Reset password with token
   * POST /api/account/reset-password
   * @remarks AllowAnonymous - No auth required
   */
  async resetPassword(request: ResetPasswordRequest): Promise<Response<boolean>> {
    return this.post<boolean, ResetPasswordRequest>(`${this.basePath}/reset-password`, request);
  }
}

// Export singleton instance
export const accountApi = new AccountApi();

export default AccountApi;
